"use client";

import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { IWhyBlock } from "@/sanity/pages/PageHomeTemplate";
import RiveAnimation, { Fit } from "@/components/global/RiveAnimation";
import TradersMosaic from "@/components/global/TradersMosaic";
import GradualBlur from "@/shadcn/GradualBlur";
import { FadeIn } from "@/components/global/utils/FadeIn";
import { PipeChip } from "./_shared";

/**
 * `title` is richtext on the home page, but the leaderboard's steps keep theirs
 * as a plain `string` (no emphasis to carry, per the house rule in
 * PageHomeTemplate). Widened here rather than in `IWhyBlock` so the home's own
 * type is untouched — same `Array.isArray` split MasterClassSection uses.
 */
type WhyBlock = Omit<IWhyBlock, "title"> & { title?: string | PortableTextBlock[] };

interface WhyWCESectionProps {
  chip?: string;
  title?: PortableTextBlock[];
  subtitle?: string;
  marquee?: string[];
  blocks?: WhyBlock[];
  variant?: "home" | "plain";
}

const ROMAN = ["I", "II", "III", "IV", "V", "VI"];

function FeatureBlock({
  block,
  index,
  isLast,
  variant,
}: {
  block: WhyBlock;
  index: number;
  isLast: boolean;
  variant: "home" | "plain";
}) {
  const imageLeft = block.imagePosition === "left";
  const homeArt = variant === "home";

  const text = (
    <FadeIn className="flex flex-col items-start w-full shrink-0 gap-[2rem] lg:w-[60.5rem]">
      {(block.chip || !homeArt) && (
        <div className="flex items-center gap-[1rem] lg:gap-[2rem]">
          <span
            className={`flex justify-center items-center font-medium rounded-[0.4rem] bg-[#98d8ff] px-[0.8rem] py-[0.2rem] font-jetbrainsmono text-[1.4rem] text-black/90 lg:rounded-[0.5rem] lg:px-[1rem] lg:py-[0.25rem] lg:text-[1.75rem]${
              homeArt
                ? ""
                : " max-lg:rounded-[0.5rem] max-lg:px-[1rem] max-lg:py-[0.25rem] max-lg:text-[1.75rem] max-lg:leading-[2.25rem]"
            }`}>
            {String(index + 1).padStart(2, "0")}
          </span>
          {block.chip && (
            <p className="m-0 leading-none text-black uppercase font-[400] text-[1.4rem] font-jetbrainsmono lg:text-[1.74rem]">
              {block.chip}
            </p>
          )}
        </div>
      )}
      {Array.isArray(block.title) ? (
        <div className="richtext richtext-start [&_br]:hidden lg:[&_br]:block prose-p:font-news prose-p:text-[3rem] prose-p:leading-[1.1] prose-p:tracking-[-0.1rem] prose-p:text-[#0a0a0a] prose-p:m-0 lg:prose-p:text-[5rem] lg:prose-p:leading-[1.05] prose-em:font-news prose-em:italic prose-headings:font-news prose-headings:text-[3.75rem] prose-headings:leading-[1.1] prose-headings:tracking-[-0.1rem] prose-headings:text-[#0a0a0a] lg:prose-headings:text-[5rem] lg:prose-headings:leading-none">
          <PortableText
            value={block.title}
            components={richTextComponent}
          />
        </div>
      ) : block.title ? (
        <h3 className="m-0 font-news font-normal text-[3.5rem] leading-[1.1] tracking-[-0.1rem] text-[#0a0a0a] max-lg:tracking-[-0.25rem] max-lg:text-black/90 lg:text-[5rem] lg:leading-none">
          {block.title}
        </h3>
      ) : null}
      <div className="w-full border-t border-t-black/10 h-[.125rem]" />
      {block.description && (
        <p
          className={`m-0 font-geist text-[1.8rem] leading-[1.5] tracking-[-0.02rem] text-black/50 lg:text-[2rem]${
            homeArt
              ? ""
              : " max-lg:text-[2.25rem] max-lg:tracking-normal max-lg:text-black/60"
          }`}>
          {block.description}
        </p>
      )}
      {block.bullets && (
        <div className="flex flex-col w-full gap-[1.75rem] lg:gap-[1rem]">
          {block.bullets.map((item, index) => (
            <div
              key={index}
              className="flex items-center bg-[#6ABEF2]/10 rounded-[0.75rem] p-[1rem] gap-[1.25rem]">
              {item.icon && (
                <div className="w-[3rem] h-[3rem]">
                  <Image
                    src={item.icon}
                    alt=""
                    className="!w-full !h-full"
                  />
                </div>
              )}

              <svg
                width="1"
                height="24"
                viewBox="0 0 1 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <line
                  opacity="0.1"
                  x1="0.5"
                  y1="2.18559e-08"
                  x2="0.499999"
                  y2="24"
                  stroke="black"
                />
              </svg>
              <div>
                <p
                  className={`m-0 text-black text-[1.9rem]${
                    homeArt
                      ? ""
                      : " max-lg:text-[2rem] max-lg:leading-[2.625rem] max-lg:text-black/90"
                  }`}>
                  {item?.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="w-full mt-[1rem] lg:w-fit">
        <CTAButton data={block.cta ?? {}} />
      </div>
    </FadeIn>
  );

  const hasVideo = Boolean(block.video?.muxVideo?.playbackId);

  const image = (
    // 73.75rem is the home's art frame; the leaderboard's product mocks are
    // 532px square in its own Figma (4074:109419), hence 66.5rem there.
    <div
      className={`relative w-full overflow-clip h-[39.8rem] rounded-[1.2rem] shrink-0 lg:rounded-[1.5rem] ${
        homeArt ? "lg:h-[73.75rem] lg:w-[73.75rem]" : "lg:h-[66.5rem] lg:w-[66.5rem]"
      }`}>
      {homeArt && index === 0 ? (
        <>
          <RiveAnimation
            src="/home/wce.riv"
            fit={Fit.Cover}
            className="absolute inset-0 w-full h-full"
          />
          <GradualBlur
            position="left"
            width="12rem"
            strength={2}
            divCount={8}
            zIndex={10}
          />
          <div className="absolute left-0 h-full bg-gradient-to-l from-transparent to-[#eee]/60 w-[12rem]" />

          <div className="absolute right-0 h-full bg-gradient-to-r from-transparent to-[#eee]/60 w-[12rem]" />
          <GradualBlur
            position="right"
            width="12rem"
            strength={2}
            divCount={8}
            zIndex={10}
          />
        </>
      ) : homeArt && isLast ? (
        <TradersMosaic className="absolute inset-0 w-full h-full" />
      ) : hasVideo ? (
        <HLSVideoPlayer
          source={block.video!}
          autoPlay
          muted
          loop
          playsInline
          poster={block.image?.src}
          className="object-cover absolute inset-0 w-full h-full"
        />
      ) : block.image ? (
        <Image
          src={block.image}
          alt=""
          quality={100}
          fill
          className="object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-black/[0.08] to-black/[0.02]" />
      )}
    </div>
  );

  return (
    <div
      className={`flex flex-col gap-[3.2rem] lg:items-center lg:justify-between lg:gap-[6rem] ${
        imageLeft ? "lg:flex-row" : "lg:flex-row-reverse"
      }`}>
      {image}
      {text}
    </div>
  );
}

export default function WhyWCESection({
  chip,
  title,
  subtitle,
  marquee,
  blocks,
  variant = "home",
}: WhyWCESectionProps) {
  return (
    <section className="w-full bg-white py-[8rem] lg:py-[15rem]">
      <div
        className={`flex flex-col mx-auto max-w-full w-[min(150rem,100%)] px-[1.6rem] lg:px-0 ${
          variant === "plain" ? "gap-[6rem]" : "gap-[4rem]"
        }`}>
        {(chip || title || subtitle) && (
          <div className="flex flex-col items-center text-center gap-[1rem]">
            {chip && (
              <FadeIn delay={0}>
                <PipeChip color="#0a0a0a">{chip}</PipeChip>
              </FadeIn>
            )}
            {title && (
              <FadeIn
                delay={0.1}
                // The `plain` header runs the full column width, which on
                // mobile leaves the last word of a two-line title stranded on
                // its own line. Cap it to the same 38.125rem the rest of the
                // page's mobile headers sit in; desktop is unchanged.
                className={`richtext richtext-center prose-p:font-news prose-p:text-[4rem] prose-p:leading-[1.1] prose-p:tracking-[-0.2rem] prose-p:text-black/90 lg:prose-p:text-[6rem] prose-em:font-news prose-em:italic prose-headings:font-news prose-headings:text-[4.5rem] prose-headings:leading-[1.1] prose-headings:tracking-[-0.2rem] prose-headings:text-black/90 lg:prose-headings:text-[6rem] ${
                  variant === "plain" ? "w-[min(38.125rem,100%)] lg:w-auto" : ""
                }`}>
                <PortableText
                  value={title}
                  components={richTextComponent}
                />
              </FadeIn>
            )}
            {subtitle && (
              <FadeIn
                delay={0.15}
                className={`w-[min(88rem,100%)] ${
                  variant === "plain" ? "lg:-mt-[0.375rem]" : ""
                }`}>
                <p className="m-0 font-geist text-[2.25rem] leading-[1.5] text-black/60">
                  {subtitle}
                </p>
              </FadeIn>
            )}
          </div>
        )}
        <div className="flex flex-col w-full gap-[5rem] lg:gap-[10rem]">
          {blocks?.map((b, i) => (
            <FeatureBlock
              key={i}
              block={b}
              index={i}
              isLast={i === (blocks?.length ?? 0) - 1}
              variant={variant}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
